import * as React from "react";
import { FormGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import { WorkflowNode, ConnectedAiConfig } from "../../types";
import useAIConfigs from "@/hooks/useAIConfigs";
import useNetSuiteConfigs from "@/hooks/useNetSuiteConfigs";
import useMcpServers from "@/hooks/useMcpServers";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

// AI providers that support MCP tool calling
const MCP_SUPPORTED_PROVIDERS = ["claude", "openai", "azure-openai", "anthropic"];

export default function NetsuiteAIEditor({ node, onUpdate }: Props) {
  const { aiConfigurations } = useAIConfigs();
  const { items: netSuiteConfigs } = useNetSuiteConfigs(0);
  const { items: mcpServers } = useMcpServers();

  const aiOptions = aiConfigurations.map((ai) => ({
    value: ai.id,
    label: ai.name
  }));

  const nsOptions = netSuiteConfigs.map((ns) => ({
    value: ns.id,
    label: ns.displayName || ns.company || `Config #${ns.id}`
  }));

  const selectedAiConfig = aiConfigurations.find((ai) => ai.id === node.aiConfigId);
  const enabledMcpServers = mcpServers.filter((s) => s.enabled);

  const providerName = (selectedAiConfig as { provider?: string } | undefined)?.provider?.toLowerCase() ?? "";
  const providerUnsupported =
    enabledMcpServers.length > 0 &&
    selectedAiConfig != null &&
    providerName !== "" &&
    !MCP_SUPPORTED_PROVIDERS.some((p) => providerName.includes(p));

  const selectedMcpIds: number[] = Array.isArray(node.mcpServerIds) ? node.mcpServerIds : [];

  function toggleMcpServer(id: number, checked: boolean) {
    const next = checked
      ? [...selectedMcpIds, id]
      : selectedMcpIds.filter((x) => x !== id);
    onUpdate({ mcpServerIds: next.length > 0 ? next : undefined });
  }

  // ── Orchestration: "Can delegate to" ──
  const connectedAiConfigs: ConnectedAiConfig[] = Array.isArray(node.connectedAiConfigs)
    ? node.connectedAiConfigs
    : [];

  const otherAiOptions = aiConfigurations.filter((ai) => ai.id !== node.aiConfigId);

  function addDelegate(aiConfigId: number) {
    const ai = aiConfigurations.find((a) => a.id === aiConfigId);
    if (!ai) return;
    if (connectedAiConfigs.some((c) => c.aiConfigId === aiConfigId)) return;
    const next: ConnectedAiConfig[] = [
      ...connectedAiConfigs,
      { aiConfigId, name: ai.name, when: "", mcpServerIds: [] }
    ];
    onUpdate({ connectedAiConfigs: next });
  }

  function removeDelegate(aiConfigId: number) {
    const next = connectedAiConfigs.filter((c) => c.aiConfigId !== aiConfigId);
    onUpdate({ connectedAiConfigs: next.length > 0 ? next : undefined });
  }

  function updateDelegateField(aiConfigId: number, field: keyof ConnectedAiConfig, value: unknown) {
    const next = connectedAiConfigs.map((c) =>
      c.aiConfigId === aiConfigId ? { ...c, [field]: value } : c
    );
    onUpdate({ connectedAiConfigs: next });
  }

  function toggleDelegateMcp(aiConfigId: number, mcpId: number, checked: boolean) {
    const next = connectedAiConfigs.map((c) => {
      if (c.aiConfigId !== aiConfigId) return c;
      const ids = Array.isArray(c.mcpServerIds) ? c.mcpServerIds : [];
      const updated = checked ? [...ids, mcpId] : ids.filter((x) => x !== mcpId);
      return { ...c, mcpServerIds: updated.length > 0 ? updated : [] };
    });
    onUpdate({ connectedAiConfigs: next });
  }

  return (
    <>
      <FormGroup label="AI Config">
        <Select
          options={aiOptions}
          value={node.aiConfigId}
          onChange={(value) => {
            onUpdate({ aiConfigId: Number(value) });
          }}
        />
      </FormGroup>
      <FormGroup label="NetSuite Config">
        <Select
          options={nsOptions}
          value={node.netSuiteConfigId}
          onChange={(value) => {
            onUpdate({ netSuiteConfigId: Number(value) });
          }}
        />
      </FormGroup>

      {enabledMcpServers.length > 0 && (
        <FormGroup label="MCP Tool Servers">
          <div className="flex flex-col gap-2 mt-1">
            {providerUnsupported && (
              <p className="text-amber-700 bg-amber-50 text-xs px-2 py-1 rounded">
                MCP tools require Claude or OpenAI. The selected AI config may not support them.
              </p>
            )}
            {enabledMcpServers.map((server) => {
              const checked = selectedMcpIds.includes(server.id);
              return (
                <label
                  key={server.id}
                  className={`flex gap-2.5 cursor-pointer rounded border p-2.5 transition-colors ${
                    checked
                      ? "border-blue-300 bg-blue-50"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 shrink-0"
                    checked={checked}
                    onChange={(e) => toggleMcpServer(server.id, e.target.checked)}
                    aria-label={`Enable MCP server: ${server.name}`}
                  />
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{server.name}</span>
                      {server.toolCount > 0 && (
                        <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                          {server.toolCount} {server.toolCount === 1 ? "tool" : "tools"}
                        </span>
                      )}
                    </div>
                    {server.description && (
                      <span className="text-xs text-slate-500">{server.description}</span>
                    )}
                    {server.toolNames.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {server.toolNames.slice(0, 6).map((t) => (
                          <span
                            key={t}
                            className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded font-mono"
                          >
                            {t}
                          </span>
                        ))}
                        {server.toolNames.length > 6 && (
                          <span className="text-xs text-slate-400 px-1 py-0.5">
                            +{server.toolNames.length - 6} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </FormGroup>
      )}

      {/* Orchestration: specialist agents this AI can hand off to */}
      {otherAiOptions.length > 0 && (
        <FormGroup label="Can Delegate To">
          <p className="text-xs text-slate-500 mb-2">
            Select specialist AI agents this chatbot can hand conversations off to. The AI will use
            a tool to transfer when the customer's question matches a specialist's domain.
          </p>

          {connectedAiConfigs.length > 0 && (
            <div className="flex flex-col gap-3 mb-3">
              {connectedAiConfigs.map((delegate) => {
                const ai = aiConfigurations.find((a) => a.id === delegate.aiConfigId);
                return (
                  <div
                    key={delegate.aiConfigId}
                    className="border border-blue-200 bg-blue-50 rounded p-3 flex flex-col gap-2"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-900">
                        {ai?.name ?? `AI Config #${delegate.aiConfigId}`}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeDelegate(delegate.aiConfigId)}
                        className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                        aria-label={`Remove ${ai?.name ?? "agent"} from delegates`}
                      >
                        Remove
                      </button>
                    </div>
                    <label className="flex flex-col gap-1">
                      <span className="text-xs text-slate-600">When to delegate</span>
                      <input
                        type="text"
                        className="input text-xs"
                        placeholder='e.g. "when customer asks about orders or shipping"'
                        value={delegate.when ?? ""}
                        onChange={(e) =>
                          updateDelegateField(delegate.aiConfigId, "when", e.target.value)
                        }
                      />
                    </label>
                    {enabledMcpServers.length > 0 && (
                      <div>
                        <span className="text-xs text-slate-600 block mb-1">
                          MCP servers for this specialist
                        </span>
                        <div className="flex flex-col gap-1">
                          {enabledMcpServers.map((server) => {
                            const checked = (delegate.mcpServerIds ?? []).includes(server.id);
                            return (
                              <label
                                key={server.id}
                                className="flex items-center gap-2 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  className="w-3.5 h-3.5 shrink-0"
                                  checked={checked}
                                  onChange={(e) =>
                                    toggleDelegateMcp(
                                      delegate.aiConfigId,
                                      server.id,
                                      e.target.checked
                                    )
                                  }
                                  aria-label={`Enable ${server.name} for ${ai?.name ?? "specialist"}`}
                                />
                                <span className="text-xs text-slate-700">{server.name}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <Select
            options={[
              { value: 0, label: "— Add specialist agent —" },
              ...otherAiOptions
                .filter((ai) => !connectedAiConfigs.some((c) => c.aiConfigId === ai.id))
                .map((ai) => ({ value: ai.id, label: ai.name }))
            ]}
            value={0}
            onChange={(value) => {
              if (Number(value) > 0) addDelegate(Number(value));
            }}
          />
        </FormGroup>
      )}
    </>
  );
}
